import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Container, Box, CircularProgress } from '@mui/material';
import { theme } from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Header } from './components/common/Header';
import { Footer } from './components/common/Footer';
// Auth pages
import { SignIn } from './pages/auth/SignInPage';
import { SignUp } from './pages/auth/SignUpPage';

// Main pages
import { DashboardPage } from './pages/main/DashboardPage';
import { StrategiesPage } from './pages/main/StrategiesPage';
import { Marketplace } from './pages/main/MarketplacePage';
import { Labs } from './pages/main/LabsPage';
import { PortfolioPage } from './pages/main/PortfolioPage';
import { Landing } from './pages/main/LandingPage';

// Strategy pages
import { StrategiesListPage } from './pages/strategy/StrategiesListPage';
import { StrategyBuilderPage } from './pages/strategy/StrategyBuilderPage';

// Config pages
import { Profile } from './pages/config/ProfilePage';
import { BrokerConfigPage } from './pages/config/BrokerConfigPage';
import SettingsPage from './pages/config/SettingsPage';

// Admin pages
import AdminKrakenPage from './pages/admin/AdminKrakenPage';
import AdminBinanceUSPage from './pages/admin/AdminBinanceUSPage';
import AdminUniswapPage from './pages/admin/AdminUniswapPage';
import AdminCoinbasePage from './pages/admin/AdminCoinbasePage';
import AdminRobinhoodPage from './pages/admin/AdminRobinhoodPage';
import KrakenTestPage from './pages/admin/KrakenTestPage';
import AdminInitPage from './pages/admin/AdminInitPage';

// Component imports
import { AlpacaConfig } from './components/broker/AlpacaConfig';
import { TDAmeritradeConfig } from './components/broker/TDAmeritradeConfig';
import { CoinbaseConfig } from './components/broker/CoinbaseConfig';
import { KrakenConfig } from './components/broker/KrakenConfig';
import { BinanceUSConfig } from './components/broker/BinanceUSConfig';
import CoinbaseSetupGuide from './components/CoinbaseSetupGuide';

// PrivateRoute component to protect routes that require authentication
interface PrivateRouteProps {
  component: React.ComponentType<any>;
  [key: string]: any;
}

const PrivateRoute = ({ component: Component, ...rest }: PrivateRouteProps) => {
  const { state } = useAuth();
  const navigate = useNavigate();
  
  // Use effect for navigation when user is not authenticated
  useEffect(() => {
    if (!state.user && !state.loading) {
      console.log('User not authenticated, redirecting to sign-in page');
      navigate('/sign-in', { replace: true });
    }
  }, [state.user, state.loading, navigate]);
  
  // If still loading, show loading indicator
  if (state.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // If user is not authenticated, show loading while redirecting
  if (!state.user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // User is authenticated, render the protected component
  return <Component {...rest} />;
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Box sx={{ 
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            position: 'relative',
            width: '100%'
          }}>
            <Header />
            <Container 
              maxWidth="lg" 
              sx={{ 
                mt: 4,
                mb: 4,
                flex: 1,
                position: 'relative',
                overflow: 'visible'
              }}
            >
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/sign-in" element={<SignIn />} />
                <Route path="/signin" element={<Navigate to="/sign-in" />} />
                <Route path="/sign-up" element={<SignUp />} />
                <Route path="/signup" element={<Navigate to="/sign-up" />} />
                <Route
                  path="/dashboard"
                  element={<PrivateRoute component={DashboardPage} />}
                />
                <Route
                  path="/portfolio"
                  element={<PrivateRoute component={PortfolioPage} />}
                />
                <Route
                  path="/strategies"
                  element={<PrivateRoute component={StrategiesListPage} />}
                />
                <Route
                  path="/marketplace"
                  element={<PrivateRoute component={Marketplace} />}
                />
                <Route
                  path="/labs"
                  element={<PrivateRoute component={Labs} />}
                />
                <Route
                  path="/profile"
                  element={<PrivateRoute component={Profile} />}
                />
                <Route
                  path="/settings"
                  element={<PrivateRoute component={SettingsPage} />}
                />
                <Route
                  path="/broker-config"
                  element={<PrivateRoute component={BrokerConfigPage} />}
                />
                <Route
                  path="/alpaca-config"
                  element={<PrivateRoute component={AlpacaConfig} onConfigSaved={() => {}} />}
                />
                <Route
                  path="/tdameritrade-config"
                  element={<PrivateRoute component={TDAmeritradeConfig} onConfigSaved={() => {}} />}
                />
                <Route
                  path="/coinbase-config"
                  element={<PrivateRoute component={CoinbaseConfig} onConfigSaved={() => {}} />}
                />
                <Route
                  path="/coinbase-setup"
                  element={<PrivateRoute component={CoinbaseSetupGuide} />}
                />
                <Route
                  path="/kraken-config"
                  element={<PrivateRoute component={KrakenConfig} onConfigSaved={() => {}} />}
                />
                <Route
                  path="/binanceus-config"
                  element={<PrivateRoute component={BinanceUSConfig} onConfigSaved={() => {}} />}
                />
                <Route
                  path="/admin-kraken"
                  element={<PrivateRoute component={AdminKrakenPage} />}
                />
                <Route
                  path="/admin-binanceus"
                  element={<PrivateRoute component={AdminBinanceUSPage} />}
                />
                <Route
                  path="/admin-uniswap"
                  element={<PrivateRoute component={AdminUniswapPage} />}
                />
                <Route
                  path="/admin-init"
                  element={<PrivateRoute component={AdminInitPage} />}
                />
                <Route
                  path="/admin-coinbase"
                  element={<PrivateRoute component={AdminCoinbasePage} />}
                />
                <Route
                  path="/admin-robinhood"
                  element={<PrivateRoute component={AdminRobinhoodPage} />}
                />
                <Route
                  path="/kraken-test"
                  element={<KrakenTestPage />}
                />
                {/* Strategy Routes */}
                <Route
                  path="/strategies-list"
                  element={<PrivateRoute component={StrategiesListPage} />}
                />
                <Route
                  path="/strategy/:id"
                  element={<PrivateRoute component={StrategyBuilderPage} />}
                />
                <Route
                  path="/strategy/new"
                  element={<PrivateRoute component={StrategyBuilderPage} />}
                />
              </Routes>
            </Container>
            <Footer />
          </Box>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
